import { Link } from 'react-router-dom';
import type { ContentItem } from '@/lib/content';
import { typeLabels } from '@/lib/content';

interface ContentCardProps {
  item: ContentItem;
  variant?: 'poster' | 'wide' | 'person';
  index?: number;
}

export default function ContentCard({ item, variant = 'poster', index = 0 }: ContentCardProps) {
  const delay = `${Math.min(index, 6) * 60}ms`;

  if (variant === 'wide') {
    return (
      <Link
        to={`/detail/${item.id}`}
        className="group grid grid-cols-1 gap-4 rounded-lg border border-background-200/80 bg-background-100 p-3 transition-colors duration-300 hover:border-primary-500/40 sm:grid-cols-[190px_1fr] md:grid-cols-[240px_1fr] md:p-4 animate-fade-up"
        style={{ animationDelay: delay }}
      >
        <div className="relative h-40 w-full overflow-hidden rounded-md bg-background-200 sm:h-full sm:min-h-[140px]">
          <img
            src={item.image}
            alt={`${item.title} 剧照`}
            title={`${item.title} - 我的朋友他的妻子`}
            className="h-full w-full object-cover object-top transition-transform duration-700 ease-smooth group-hover:scale-[1.04]"
          />
        </div>
        <div className="flex min-w-0 flex-col justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-accent-900/60 px-2.5 py-0.5 text-[11px] text-accent-200">
                {typeLabels[item.type]}
              </span>
              <span className="text-[11px] text-foreground-500">{item.dateLabel}</span>
            </div>
            <h3 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-400 md:text-lg">
              {item.title}
            </h3>
            <p className="mt-1 text-xs text-foreground-500">{item.subtitle}</p>
            <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-400">
              {item.excerpt}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {item.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="rounded-full border border-secondary-700/50 px-2 py-0.5 text-[11px] text-foreground-500"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'person') {
    return (
      <Link
        to={`/detail/${item.id}`}
        className="group block overflow-hidden rounded-lg border border-background-200/80 bg-background-100 transition-colors duration-300 hover:border-primary-500/40 animate-fade-up"
        style={{ animationDelay: delay }}
      >
        <div className="relative h-56 w-full overflow-hidden bg-background-200 md:h-64">
          <img
            src={item.image}
            alt={`${item.title} 演员肖像`}
            title={`${item.title} - 我的朋友他的妻子 演员`}
            className="h-full w-full object-cover object-top transition-transform duration-700 ease-smooth group-hover:scale-105"
          />
          <span className="absolute bottom-2 left-2 rounded-full bg-background-50/85 px-2.5 py-0.5 text-[11px] text-primary-300 backdrop-blur">
            {item.subtitle.split('｜')[0]}
          </span>
        </div>
        <div className="p-4">
          <h3 className="font-heading text-base font-bold text-foreground-950 transition-colors group-hover:text-primary-400">
            {item.title}
          </h3>
          <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground-500">
            {item.excerpt}
          </p>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${item.id}`}
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200/80 bg-background-100 transition-colors duration-300 hover:border-primary-500/40 animate-fade-up"
      style={{ animationDelay: delay }}
    >
      <div className="relative h-44 w-full overflow-hidden bg-background-200 md:h-48">
        <img
          src={item.image}
          alt={`${item.title} 剧照`}
          title={`${item.title} - 我的朋友他的妻子 剧情`}
          className="h-full w-full object-cover object-top transition-transform duration-700 ease-smooth group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/10 to-transparent"></div>
        <span className="absolute left-3 top-3 rounded-full bg-background-50/80 px-2.5 py-0.5 text-[11px] text-primary-300 backdrop-blur">
          {item.tags[0]}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-4">
        <span className="text-[11px] tracking-wide text-foreground-500">{item.dateLabel}</span>
        <h3 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-400">
          {item.title}
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-400">
          {item.excerpt}
        </p>
        <span className="mt-3 inline-flex items-center gap-1 text-xs text-foreground-500 transition-colors group-hover:text-primary-400">
          阅读详情
          <i className="ri-arrow-right-line"></i>
        </span>
      </div>
    </Link>
  );
}